import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { sessionsApi } from '@/api/endpoints';
import { tokenStore } from '@/store/auth';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

const SESSIONS_KEY = ['me', 'sessions'] as const;

/* One row per refresh-token family — roughly one per device. */
export function SessionsCard() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: SESSIONS_KEY,
    queryFn: sessionsApi.list,
    staleTime: 30_000,
  });

  const revokeOne = useMutation({
    mutationFn: (familyId: string) => sessionsApi.revokeOne(familyId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: SESSIONS_KEY }),
  });

  /*
    Signing out everywhere includes THIS device. The access token stays valid
    until it expires, so a UI that kept using it would keep showing a session
    the user was just told had been revoked — clear the store and send them to
    /login rather than leaving a live-looking session behind.
  */
  const revokeAll = useMutation({
    mutationFn: () => sessionsApi.revokeAll(),
    onSuccess: () => {
      tokenStore.clear();
      navigate('/login');
    },
  });

  const items = data?.items ?? [];

  return (
    <Card title="Active sessions">
      {isLoading && <p className="text-sm text-white/45">Loading…</p>}
      {isError && <p className="text-sm text-red-400">Could not load your sessions.</p>}

      {!isLoading && !isError && items.length === 0 && (
        <p className="text-sm text-white/45">No other active sessions.</p>
      )}

      {items.length > 0 && (
        <ul className="flex flex-col divide-y divide-white/[0.07]">
          {items.map((s) => (
            <li key={s.familyId} className="flex items-start justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <div className="text-sm truncate">{s.issuedUserAgent || 'Unknown device'}</div>
                <div className="text-xs text-white/40">
                  Signed in {fmt(s.sessionStartedAt)} · last used {fmt(s.lastActivityAt)}
                  {s.issuedIp ? ` · ${s.issuedIp}` : ''}
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="shrink-0"
                loading={revokeOne.isPending && revokeOne.variables === s.familyId}
                onClick={() => revokeOne.mutate(s.familyId)}
              >
                Sign out
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Button
        variant="danger"
        size="sm"
        className="mt-4 w-full"
        loading={revokeAll.isPending}
        onClick={() => revokeAll.mutate()}
      >
        Sign out everywhere
      </Button>
      <p className="mt-2 text-xs text-white/35">
        This signs out this device too, and you will need to log in again.
      </p>
    </Card>
  );
}

function fmt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
